"use client";

import { useLingui } from "@lingui/react/macro";
import { MicIcon, MicOffIcon } from "lucide-react";
import { PromptInputButton } from "@/components/ai-elements/prompt-input";
import { cn } from "@/lib/utils";
import { useChatPromptSurfaceContext } from "./chat-prompt-surface-context";

/**
 * チャット入力エリアの音声入力ボタン。
 *
 * 録音の開始/停止や確定テキストの追記・仮入力表示は `ChatPromptSurface` provider が
 * 所有しており、この component は共有 state を購読して表示するだけの薄い UI に留める。
 * starter / composer / 右サイドパネルのいずれも同じ入力本体 (`ChatPromptSurfaceInput`)
 * を経由するため、この 1 箇所で全チャット画面を賄う。
 *
 * - 未対応ブラウザや送信不可状態ではボタンをグレーアウト（disabled）にし、エラーには
 *   しない。
 * - 録音中はパルスアニメーションで視覚的にフィードバックする。
 */
export function ChatPromptSpeechButton() {
  const { t } = useLingui();
  const { isInteractive, speechSupported, speechIsListening, toggleSpeech } =
    useChatPromptSurfaceContext("ChatPromptSpeechButton");

  // surface が送信状態へ移っても、停止完了までは利用者が手動中断できる状態を保つ。
  const disabled = !speechSupported || (!isInteractive && !speechIsListening);
  const title = !speechSupported
    ? t`Voice input is not supported in this browser`
    : speechIsListening
      ? t`Stop voice input`
      : t`Voice input`;

  return (
    <PromptInputButton
      type="button"
      onClick={toggleSpeech}
      variant="ghost"
      disabled={disabled}
      // スクリーンリーダーで「音声入力」と読み上げられるよう、状態に依らず固定ラベルにする。
      aria-label={t`Voice input`}
      aria-pressed={speechIsListening}
      title={title}
      className={cn(
        "rounded-full text-muted-foreground transition-colors hover:text-foreground",
        speechIsListening &&
          "animate-pulse bg-destructive/10 text-destructive hover:text-destructive motion-reduce:animate-none",
      )}
    >
      {speechSupported ? (
        <MicIcon className="h-4 w-4" />
      ) : (
        <MicOffIcon className="h-4 w-4" />
      )}
    </PromptInputButton>
  );
}
